interface PaginationProps {
  page: number;
  totalPages: number;
  onChange: (page: number) => void;
}

function buildPages(page: number, totalPages: number): (number | 'ellipsis')[] {
  if (totalPages <= 7) {
    return Array.from({ length: totalPages }, (_, index) => index + 1);
  }
  const pages: (number | 'ellipsis')[] = [1];
  const start = Math.max(2, page - 1);
  const end = Math.min(totalPages - 1, page + 1);
  if (start > 2) pages.push('ellipsis');
  for (let current = start; current <= end; current += 1) pages.push(current);
  if (end < totalPages - 1) pages.push('ellipsis');
  pages.push(totalPages);
  return pages;
}

export default function Pagination({ page, totalPages, onChange }: PaginationProps) {
  if (totalPages <= 1) return null;
  const pages = buildPages(page, totalPages);

  const go = (target: number) => {
    if (target < 1 || target > totalPages || target === page) return;
    onChange(target);
  };

  return (
    <nav className="flex items-center justify-center gap-2 flex-wrap" aria-label="分页导航">
      <button
        type="button"
        onClick={() => go(page - 1)}
        disabled={page === 1}
        className="h-10 min-w-10 px-3 rounded-md border border-background-300/70 text-sm text-foreground-700 flex items-center justify-center gap-1 disabled:opacity-35 disabled:cursor-not-allowed hover:border-primary-500/60 hover:text-foreground-950 transition-colors cursor-pointer whitespace-nowrap"
        aria-label="上一页"
      >
        <i className="ri-arrow-left-s-line text-lg w-5 h-5 flex items-center justify-center" />
        <span className="hidden sm:inline">上一页</span>
      </button>

      {pages.map((item, index) =>
        item === 'ellipsis' ? (
          <span
            key={`ellipsis-${index}`}
            className="w-8 h-10 flex items-end justify-center text-foreground-500 text-sm"
          >
            ···
          </span>
        ) : (
          <button
            key={item}
            type="button"
            onClick={() => go(item)}
            aria-current={item === page ? 'page' : undefined}
            className={`h-10 min-w-10 px-2 rounded-md text-sm transition-colors cursor-pointer ${
              item === page
                ? 'bg-primary-500 text-background-50 dark:text-foreground-950 font-medium'
                : 'border border-background-300/70 text-foreground-700 hover:border-primary-500/60 hover:text-foreground-950'
            }`}
          >
            {item}
          </button>
        ),
      )}

      <button
        type="button"
        onClick={() => go(page + 1)}
        disabled={page === totalPages}
        className="h-10 min-w-10 px-3 rounded-md border border-background-300/70 text-sm text-foreground-700 flex items-center justify-center gap-1 disabled:opacity-35 disabled:cursor-not-allowed hover:border-primary-500/60 hover:text-foreground-950 transition-colors cursor-pointer whitespace-nowrap"
        aria-label="下一页"
      >
        <span className="hidden sm:inline">下一页</span>
        <i className="ri-arrow-right-s-line text-lg w-5 h-5 flex items-center justify-center" />
      </button>
    </nav>
  );
}